import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../../msalConfig';
import { useUser } from '../../contexts/UserContext';

const ProtectedRouteDX = ({ children, title = 'DX Style Initial Plan' }) => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const { fetchUserInfo, user } = useUser();

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      setIsLoading(false);
    }
  }, [inProgress]);

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None && accounts.length > 0) {
      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      }).then((response) => {
        localStorage.setItem('authToken', response.accessToken);
        if (!user) {
          fetchUserInfo();
        }
      }).catch((error) => {
        console.error('ProtectedRouteDX: Access Token 획득 실패:', error);
        if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required' || error.errorCode === 'login_required') {
          instance.acquireTokenRedirect({
            ...loginRequest,
            account: accounts[0]
          }).catch((redirectError) => {
            console.error('ProtectedRouteDX: 리디렉트 토큰 획득 실패:', redirectError);
          });
        }
      });
    }
  }, [isAuthenticated, inProgress, accounts, user, fetchUserInfo, instance]);

  // 로딩 중
  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>{title}</h1>
          <div style={styles.divider} />
          <div style={styles.spinner} />
          <p style={styles.loadingText}>로그인 진행 중...</p>
        </div>
        <style>{keyframes}</style>
      </div>
    );
  }

  // 미인증
  if (!isAuthenticated) {
    const handleLogin = async () => {
      if (inProgress !== InteractionStatus.None) return;
      try {
        await instance.loginRedirect(loginRequest);
      } catch (error) {
        console.error("로그인 실패:", error);
      }
    };

    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>{title}</h1>
          <div style={styles.divider} />
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '8px' }}>
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM15.1 8H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" fill="#999"/>
          </svg>
          <p style={styles.desc}>로그인이 필요합니다</p>
          <button
            onClick={handleLogin}
            disabled={inProgress !== InteractionStatus.None}
            style={{
              ...styles.button,
              cursor: inProgress !== InteractionStatus.None ? 'not-allowed' : 'pointer',
              opacity: inProgress !== InteractionStatus.None ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (inProgress === InteractionStatus.None) {
                e.currentTarget.style.backgroundColor = '#2a0cb0';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3710d3';
            }}
          >
            {inProgress !== InteractionStatus.None ? '로그인 진행 중...' : 'Microsoft SSO 로그인'}
          </button>
          <p style={styles.contact}>권한 문의: AX팀 국준호 과장</p>
        </div>
        <style>{keyframes}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRouteDX;

const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    width: '100vw',
    backgroundColor: '#f5f5f5',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '48px 56px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    minWidth: '380px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  divider: {
    borderRadius: '2px',
    margin: '20px 0 5px 0',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e8e8e8',
    borderTop: '3px solid #3710d3',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    fontSize: '14px',
    color: '#888',
    margin: '20px 0 0 0',
    fontWeight: 400,
  },
  desc: {
    fontSize: '15px',
    color: '#666',
    margin: '0 0 28px 0',
    textAlign: 'center',
  },
  button: {
    padding: '14px 48px',
    backgroundColor: '#3710d3',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 500,
    transition: 'background-color 0.2s ease',
    letterSpacing: '0.3px',
  },
  contact: {
    fontSize: '12px',
    color: '#aaa',
    margin: '24px 0 0 0',
  },
};
